'use client';

import {Icon} from '@components/@icons';
import {Breakpoints} from '@config/breakpoints';
import {Autocomplete, useMediaQuery} from '@mui/material';
import classNames from 'classnames';
import {
    ForwardedRef,
    ReactNode,
    SyntheticEvent,
    forwardRef,
    useRef,
} from 'react';
import {mergeRefs} from 'react-merge-refs';
import {MeinInput} from '..';
import {MeinScrollbarForMui} from '../Scrollbar/MeinScrollbarForMui';
import styles from './MeinSelectSearch.module.css';
interface IProps<T> {
    options: T[];
    inputClassName?: string;
    placeholder?: string;
    onChange?: (e: SyntheticEvent<Element, Event>, value: T | null) => void;
    className?: string;
    value?: T | null;
    error?: boolean;
    helperText?: ReactNode;
    getOptionLabel: (option: T) => string;
    getOptionValue: (option: T) => string;
    label?: string;
    size?: 'small' | 'medium';
}

export const MeinSelectSearch = forwardRef(function MeinSelectSearch<T>(
    {
        options,
        inputClassName,
        className,
        error,
        helperText,
        getOptionLabel,
        getOptionValue,
        label,
        value,
        size = 'medium',
        placeholder,
        ...rest
    }: IProps<T>,
    forwardedRef: ForwardedRef<HTMLDivElement>,
) {
    const isMobile = useMediaQuery(`(max-width: ${Breakpoints.TABLET}px)`);
    const ref = useRef<HTMLInputElement>(null);

    const handleFocus = () => {
        if (!isMobile) {
            return;
        }
        setTimeout(() => {
            ref.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
        }, 300);
    };

    return (
        <Autocomplete
            onOpen={handleFocus}
            slots={{paper: MeinScrollbarForMui}}
            options={options}
            slotProps={{
                listbox: {className: styles.listBox},
                paper: {className: styles.menu},
                clearIndicator: {
                    disableRipple: true,
                    className: styles.clearIndicator,
                },
                popupIndicator: {
                    disableRipple: true,
                    className: styles.popupIndicator,
                },
            }}
            className={classNames(styles.select, className)}
            isOptionEqualToValue={(option, value) => {
                return getOptionValue(option) === getOptionValue(value);
            }}
            value={value}
            clearIcon={<Icon name="close" className={styles.adornmentIcon} />}
            popupIcon={
                <Icon
                    name="chevron-down"
                    size={22}
                    className={styles.adornmentIcon}
                />
            }
            renderOption={({className, ...props}, option) => {
                return (
                    <li
                        className={classNames(className, styles.menuItem)}
                        {...props}
                        key={getOptionValue(option)}
                    >
                        {getOptionLabel(option)}
                    </li>
                );
            }}
            renderInput={params => (
                <MeinInput
                    ref={mergeRefs([ref, forwardedRef])}
                    className={classNames(styles.input, inputClassName)}
                    error={error}
                    helperText={helperText}
                    {...params}
                    size={size}
                    label={label}
                    value={value}
                    placeholder={placeholder}
                    autoComplete="off"
                    type="search"
                />
            )}
            {...rest}
        />
    );
});

export default MeinSelectSearch;
